// src/pages/StaffPortal.jsx
// Comprehensive Staff Portal: Leave Request Form + Calendar + Leave Summary & Records
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, getDocs, addDoc, deleteDoc, query, where,
  Timestamp, serverTimestamp, onSnapshot, doc, updateDoc
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useSalonAuth } from '../contexts/SalonAuthContext';
import StaffCalendarView from '../components/staff/StaffCalendarView';
import '../styles/staff-portal.css';
import '../styles/booking-calendar.css';

// ── utilities ────────────────────────────────────────────────────────────────
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const FORM_EMPTY = {
  type: 'annual',
  startDate: '',
  endDate: '',
  timeType: 'full',
  startTime: '09:00',
  endTime: '10:00',
  reason: '',
  notes: ''
};

const timeToMinutes = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const formatTime12Hour = (t24) => {
  if (!t24) return '';
  const [h, m] = t24.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return t24;
  const per = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${per}`;
};

const generateTimeOptions = () => {
  const opts = [];
  for (let h = 8; h <= 18; h++) {
    opts.push(`${String(h).padStart(2, '0')}:00`);
  }
  return opts;
};

const calculateLeaveDays = (record) => {
  if (!record.startDate?.seconds || !record.endDate?.seconds) return 0;
  const start = new Date(record.startDate.seconds * 1000);
  const end   = new Date(record.endDate.seconds * 1000);
  let days = 0;
  const cur = new Date(start);
  while (cur <= end) {
    if (record.timeType === 'full')      days += 1;
    else if (record.timeType === 'am' || record.timeType === 'pm') days += 0.5;
    else                                 days += 1; // fallback: treat unknown timeType as full day
    cur.setDate(cur.getDate() + 1);
  }
  return days;
};

const calculateTimeOffHours = (record) => {
  if (record.type !== 'timeoff' || !record.startDate?.seconds || !record.endDate?.seconds) return 0;
  const start = new Date(record.startDate.seconds * 1000);
  const end   = new Date(record.endDate.seconds * 1000);
  let total = 0;
  const cur = new Date(start);
  while (cur <= end) {
    if (record.timeType === 'custom' && record.startTime && record.endTime) {
      total += (timeToMinutes(record.endTime) - timeToMinutes(record.startTime)) / 60;
    } else if (record.timeType === 'am') { total += 4; }
    else if (record.timeType === 'pm')   { total += 4; }
    else                                 { total += 8; }
    cur.setDate(cur.getDate() + 1);
  }
  return Math.round(total * 100) / 100;
};

// ── component ─────────────────────────────────────────────────────────────────
function StaffPortal() {
  const { salonId }                = useParams();
  const { currentUser, logout }    = useSalonAuth();
  const navigate                   = useNavigate();

  // The single canonical staffId used in all Firestore leave queries.
  // Resolved on first login and persisted back to salonUsers so future logins skip the lookup.
  const [effectiveId, setEffectiveId] = useState(null);

  // ── leave form state ────────────────────────────────────────────────────────
  const [showForm,   setShowForm]   = useState(false);
  const [formData,   setFormData]   = useState(FORM_EMPTY);
  const [saving,     setSaving]     = useState(false);
  const [formError,  setFormError]  = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // ── leave records / summary ─────────────────────────────────────────────────
  const [leaveRecords,     setLeaveRecords]     = useState([]);
  const [leaveLoading,     setLeaveLoading]     = useState(true);
  const [selectedYear,     setSelectedYear]     = useState(new Date().getFullYear());
  const [leaveAllocation,  setLeaveAllocation]  = useState({ annualLeave: 20, medicalLeave: 10 });

  // ── sign-out ────────────────────────────────────────────────────────────────
  const [signingOut, setSigningOut] = useState(false);

  // ── header alerts + profile ──────────────────────────────────────────────────
  const [approvedLeaveAlerts, setApprovedLeaveAlerts] = useState([]);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [dismissedAlertIds, setDismissedAlertIds] = useState(new Set());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef    = useRef(null);
  const leaveSummaryRef   = useRef(null);

  // ── redirect non-staff ──────────────────────────────────────────────────────
  useEffect(() => {
    if (currentUser && currentUser.role !== 'staff') {
      navigate(`/BespokeBeauty/${salonId}/admin/home`, { replace: true });
    }
  }, [currentUser, salonId, navigate]);

  // ── resolve the single canonical staffId for this staff member ───────────────
  // Priority order:
  //   1. currentUser.staffId already set (all logins after the first) — zero reads
  //   2. UID match: staff doc.id or data.staffId equals currentUser.uid (UUID-based login)
  //   3. Email match: staff.email equals currentUser.email (email-based Firebase Auth)
  //   4. Name match: staff.name equals currentUser.name (fallback for existing records
  //      that have no email stored yet, e.g. Jameson Gan before admin adds his email)
  // On a successful lookup the resolved staffId is written back to salonUsers so
  // every subsequent login hits path 1 — zero extra reads from the second login on.
  useEffect(() => {
    if (!currentUser || !salonId) return;

    if (currentUser.staffId) {
      setEffectiveId(currentUser.staffId);
      return;
    }

    const resolve = async () => {
      try {
        const snap = await getDocs(collection(db, 'staff'));
        let found = null;

        snap.forEach(d => {
          if (found) return;
          const data = d.data();
          if (data.salonId !== salonId) return;
          const candidateId = data.staffId || d.id;

          // 1. UID match (UUID-based login flow)
          if (d.id === currentUser.uid || data.staffId === currentUser.uid) {
            found = candidateId;
            return;
          }
          // 2. Email match (email/password Firebase Auth — primary path)
          if (
            data.email && currentUser.email &&
            data.email.trim().toLowerCase() === currentUser.email.trim().toLowerCase()
          ) {
            found = candidateId;
            return;
          }
          // 3. Name match (fallback for existing staff with no email stored yet)
          if (
            data.name && currentUser.name &&
            data.name.trim().toLowerCase() === currentUser.name.trim().toLowerCase()
          ) {
            found = candidateId;
          }
        });

        const resolvedId = found || currentUser.uid;
        setEffectiveId(resolvedId);

        // Persist so future logins skip this lookup entirely
        if (found) {
          try {
            await updateDoc(doc(db, 'salonUsers', currentUser.uid), { staffId: resolvedId });
          } catch {}
        }
      } catch {
        setEffectiveId(currentUser.uid);
      }
    };

    resolve();
  }, [currentUser, salonId]);

  // ── load dismissed alert IDs from localStorage ──────────────────────────────
  useEffect(() => {
    if (!salonId) return;
    try {
      const stored = localStorage.getItem(`staffDismissedAlerts_${salonId}`);
      if (stored) setDismissedAlertIds(new Set(JSON.parse(stored)));
    } catch {}
  }, [salonId]);

  // ── subscribe to this staff member's approved leave ──────────────────────────
  useEffect(() => {
    if (!effectiveId || !salonId) return;
    const q = query(
      collection(db, 'leave'),
      where('staffId', '==', effectiveId),
      where('salonId', '==', salonId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs
        .filter(d => d.data().status === 'approved')
        .map(d => {
          const data = d.data();
          return {
            id: d.id,
            type: data.type,
            startDate: data.startDate?.seconds ? new Date(data.startDate.seconds * 1000) : null,
            endDate: data.endDate?.seconds ? new Date(data.endDate.seconds * 1000) : null,
            timeType: data.timeType,
          };
        });
      setApprovedLeaveAlerts(items);
    }, () => {});
    return () => unsub();
  }, [effectiveId, salonId]);

  // ── click-outside to close profile menu ──────────────────────────────────────
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const dismissAlert = (id) => {
    setDismissedAlertIds(prev => {
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem(`staffDismissedAlerts_${salonId}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  // ── subscribe to leave records in real-time ──────────────────────────────────
  // Uses onSnapshot so the summary updates immediately when admin approves a leave,
  // without requiring the staff member to refresh the page.
  useEffect(() => {
    if (!effectiveId || !salonId) return;
    setLeaveLoading(true);
    const q = query(
      collection(db, 'leave'),
      where('staffId', '==', effectiveId),
      where('salonId', '==', salonId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const records = [];
      snap.forEach(d => {
        const data = d.data();
        if (data.startDate?.seconds) {
          const yr = new Date(data.startDate.seconds * 1000).getFullYear();
          if (yr === selectedYear) records.push({ id: d.id, ...data });
        }
      });
      records.sort((a, b) => (b.startDate?.seconds || 0) - (a.startDate?.seconds || 0));
      setLeaveRecords(records);
      setLeaveLoading(false);
    }, (err) => {
      console.error('Error loading leave:', err);
      setLeaveLoading(false);
    });
    return () => unsub();
  }, [effectiveId, salonId, selectedYear]);

  useEffect(() => {
    if (effectiveId && salonId) loadLeaveAllocation();
  }, [effectiveId, salonId]);

  const loadLeaveAllocation = async () => {
    try {
      const staffSnap = await getDocs(collection(db, 'staff'));
      staffSnap.forEach(d => {
        const data = d.data();
        if (data.salonId === salonId &&
            (d.id === effectiveId || data.staffId === effectiveId)) {
          if (data.leaveAllocation) setLeaveAllocation(data.leaveAllocation);
        }
      });
    } catch (err) {
      console.error('Error loading leave allocation:', err);
    }
  };

  // ── appointment conflict check ──────────────────────────────────────────────
  // Returns full appointment objects (not just dates) for conflict tracking and admin resolution.
  const checkAppointmentConflicts = async (startDateStr, endDateStr) => {
    const leaveStart = new Date(startDateStr); leaveStart.setHours(0, 0, 0, 0);
    const leaveEnd   = new Date(endDateStr);   leaveEnd.setHours(23, 59, 59, 999);

    try {
      const q = query(
        collection(db, 'appointments'),
        where('staffId', '==', effectiveId || currentUser?.uid),
        where('salonId', '==', salonId)
      );
      const snap = await getDocs(q);
      const conflicts = [];
      snap.forEach(d => {
        const data = d.data();
        if (data.status !== 'approved' && data.status !== 'pending') return;
        const apptStart = data.dateTimeRequested?.toDate?.()
          ?? data.startDateTime?.toDate?.()
          ?? new Date(data.dateTimeRequested || data.startDateTime);
        if (!apptStart || isNaN(apptStart.getTime())) return;
        const apptDay = new Date(apptStart); apptDay.setHours(0, 0, 0, 0);
        if (apptDay >= leaveStart && apptDay <= leaveEnd) {
          conflicts.push({
            id: d.id,
            customerName: data.customerName || data.customerEmail || 'Customer',
            customerEmail: data.customerEmail || '',
            serviceName: data.serviceRequested?.name || data.serviceName || 'Service',
            dateTime: data.dateTimeRequested || data.startDateTime || null,
            locationId: data.locationId || null,
          });
        }
      });
      return conflicts;
    } catch (err) {
      console.error('Error checking appointment conflicts:', err);
      return [];
    }
  };

  // ── form handlers ───────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'type') {
        updated.timeType  = value === 'timeoff' ? 'custom' : 'full';
        updated.startTime = '09:00';
        updated.endTime   = '10:00';
      }
      if (name === 'startTime') {
        const endMins = timeToMinutes(prev.endTime);
        const startMins = timeToMinutes(value);
        if (endMins <= startMins) {
          updated.endTime = `${String(Math.floor(startMins / 60) + 1).padStart(2, '0')}:00`;
        }
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    // Validation
    if (!formData.startDate || !formData.endDate) { setFormError('Start date and end date are required.'); return; }
    const startDate = new Date(formData.startDate);
    const endDate   = new Date(formData.endDate);
    if (startDate > endDate) { setFormError('End date must be on or after start date.'); return; }
    const oneYearOut = new Date(); oneYearOut.setFullYear(oneYearOut.getFullYear() + 1);
    if (endDate > oneYearOut) { setFormError('Leave cannot be scheduled more than 1 year in advance.'); return; }
    if (!formData.reason.trim()) { setFormError('Reason is required.'); return; }
    if (formData.type === 'timeoff' && formData.timeType === 'custom') {
      const sMins = timeToMinutes(formData.startTime);
      const eMins = timeToMinutes(formData.endTime);
      if (eMins <= sMins) { setFormError('End time must be after start time.'); return; }
      if (eMins - sMins < 60) { setFormError('Minimum time off duration is 1 hour.'); return; }
    }

    // Check existing leave conflicts
    const existingConflict = leaveRecords.find(r => {
      if (!r.startDate?.seconds || !r.endDate?.seconds) return false;
      const rs = new Date(r.startDate.seconds * 1000); rs.setHours(0, 0, 0, 0);
      const re = new Date(r.endDate.seconds * 1000);   re.setHours(0, 0, 0, 0);
      const ns = new Date(formData.startDate); ns.setHours(0, 0, 0, 0);
      const ne = new Date(formData.endDate);   ne.setHours(0, 0, 0, 0);
      if (ns > re || ne < rs) return false;

      // For two single-day leaves on the exact same date, compare time windows
      // so that complementary half-days (am + pm) are not treated as conflicts.
      if (rs.getTime() === re.getTime() && ns.getTime() === ne.getTime() && rs.getTime() === ns.getTime()) {
        const getWindow = (timeType, startTime, endTime) => {
          if (timeType === 'am') return [540, 780];
          if (timeType === 'pm') return [780, 1020];
          if (timeType === 'custom' && startTime && endTime)
            return [timeToMinutes(startTime), timeToMinutes(endTime)];
          return [0, 1440];
        };
        const [s1, e1] = getWindow(formData.timeType, formData.startTime, formData.endTime);
        const [s2, e2] = getWindow(r.timeType, r.startTime, r.endTime);
        return s1 < e2 && e1 > s2;
      }

      return true;
    });
    if (existingConflict) {
      const rs = new Date(existingConflict.startDate.seconds * 1000);
      const re = new Date(existingConflict.endDate.seconds * 1000);
      setFormError(`This leave period overlaps with an existing leave record (${rs.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${re.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}). Please adjust the dates.`);
      return;
    }

    // Check appointment conflicts — no longer blocks submission; conflicts are flagged for admin resolution.
    setSaving(true);
    const conflictingAppointments = await checkAppointmentConflicts(formData.startDate, formData.endDate);

    try {
      const isAutoApproved = formData.type !== 'timeoff';
      const leaveData = {
        leaveId:                 generateUUID(),
        type:                    formData.type,
        startDate:               Timestamp.fromDate(startDate),
        endDate:                 Timestamp.fromDate(endDate),
        timeType:                formData.timeType,
        reason:                  formData.reason.trim(),
        notes:                   formData.notes.trim(),
        staffId:                 effectiveId || currentUser?.uid,
        staffName:               currentUser?.name || '',
        salonId,
        status:                  isAutoApproved ? 'approved' : 'pending',
        createdAt:               serverTimestamp(),
        updatedAt:               serverTimestamp(),
        createdBy:               'staff',
        submittedByUid:          currentUser?.uid,
        approvedBy:              isAutoApproved ? 'auto' : null,
        hasConflicts:            conflictingAppointments.length > 0,
        conflictsResolved:       conflictingAppointments.length === 0,
        conflictingAppointments: conflictingAppointments,
      };
      if (isAutoApproved) leaveData.approvedAt = serverTimestamp();
      if (formData.type === 'timeoff' && formData.timeType === 'custom') {
        leaveData.startTime = formData.startTime;
        leaveData.endTime   = formData.endTime;
      }

      await addDoc(collection(db, 'leave'), leaveData);
      await notifyManagers(leaveData, conflictingAppointments);

      let successMsg;
      if (isAutoApproved) {
        successMsg = conflictingAppointments.length > 0
          ? 'Leave submitted and automatically approved. The admin has been notified about your conflicting appointments and will reassign them.'
          : 'Leave submitted and automatically approved.';
      } else {
        successMsg = conflictingAppointments.length > 0
          ? 'Time Off request submitted. Your manager will review it. Note: you have conflicting appointments that may need to be reassigned.'
          : 'Time Off request submitted. Your manager will review it shortly.';
      }
      setFormSuccess(successMsg);
      setShowForm(false);
      setFormData(FORM_EMPTY);
    } catch (err) {
      setFormError('Failed to submit leave request: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const notifyManagers = async (leaveData, conflictingAppointments = []) => {
    const isAutoApproved = leaveData.type !== 'timeoff';
    const hasConflicts   = conflictingAppointments.length > 0;

    // Auto-approved leave with no conflicts needs no manager notification.
    if (isAutoApproved && !hasConflicts) return;

    try {
      const snap = await getDocs(collection(db, 'salonUsers'));
      const managers = [];
      snap.forEach(d => {
        const data = d.data();
        if (data.salonId === salonId && ['owner', 'admin', 'manager'].includes(data.role))
          managers.push({ uid: d.id, ...data });
      });
      const staffName = currentUser?.name || 'A staff member';
      const startStr  = new Date(leaveData.startDate.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const endStr    = new Date(leaveData.endDate.seconds   * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const typeLabel = leaveData.type === 'annual' ? 'Annual Leave' : leaveData.type === 'medical' ? 'Medical Leave' : 'Time Off';

      for (const mgr of managers) {
        if (!isAutoApproved) {
          // Time Off: needs approval (plus note conflicts if any)
          await addDoc(collection(db, 'salonNotifications'), {
            type:         'leave_approval_pending',
            salonId,
            recipientUid: mgr.uid,
            staffName,
            staffId:      effectiveId || currentUser?.uid,
            leaveType:    typeLabel,
            leaveId:      leaveData.leaveId,
            startDate:    leaveData.startDate,
            endDate:      leaveData.endDate,
            reason:       leaveData.reason,
            hasConflicts,
            conflictCount: conflictingAppointments.length,
            message:      hasConflicts
              ? `${staffName} has requested Time Off from ${startStr} to ${endStr}. They also have ${conflictingAppointments.length} appointment(s) that may need reassignment.`
              : `${staffName} has requested Time Off from ${startStr} to ${endStr}.`,
            isRead:       false,
            createdAt:    serverTimestamp(),
          });
        } else {
          // Annual / Medical auto-approved but has appointment conflicts — needs reassignment.
          await addDoc(collection(db, 'salonNotifications'), {
            type:          'appointment_reassignment_required',
            salonId,
            recipientUid:  mgr.uid,
            staffName,
            staffId:       effectiveId || currentUser?.uid,
            leaveType:     typeLabel,
            leaveId:       leaveData.leaveId,
            startDate:     leaveData.startDate,
            endDate:       leaveData.endDate,
            conflictCount: conflictingAppointments.length,
            message:       `${staffName}'s ${typeLabel} (${startStr}–${endStr}) has been auto-approved. ${conflictingAppointments.length} appointment(s) need to be reassigned.`,
            isRead:        false,
            createdAt:     serverTimestamp(),
          });
        }
      }
    } catch (err) {
      console.error('Notify managers error:', err);
    }
  };

  // ── withdraw leave request ────────────────────────────────────────────────────
  const handleWithdrawLeave = async (record) => {
    const leaveTypeName = record.type === 'annual' ? 'Annual Leave'
      : record.type === 'medical' ? 'Medical Leave' : 'Time Off';
    const confirmed = window.confirm(
      `Withdraw this ${leaveTypeName} request? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'leave', record.id));
      // leaveRecords updates automatically via the onSnapshot listener
    } catch (err) {
      alert('Failed to withdraw leave: ' + err.message);
    }
  };

  // ── sign out ─────────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    try {
      setSigningOut(true);
      await logout();
      navigate(`/BespokeBeauty/${salonId}/admin/login`, { replace: true });
    } catch {
      setSigningOut(false);
    }
  };

  // ── display helpers ──────────────────────────────────────────────────────────
  const formatDate = (ts) => {
    if (!ts?.seconds) return '-';
    return new Date(ts.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatLeaveTimeType = (record) => {
    switch (record.timeType) {
      case 'full': return 'Full Day';
      case 'am':   return 'Morning (9AM–1PM)';
      case 'pm':   return 'Afternoon (1PM–End)';
      case 'custom':
        if (record.startTime && record.endTime)
          return `Custom (${formatTime12Hour(record.startTime)}–${formatTime12Hour(record.endTime)})`;
        return 'Custom';
      default: return record.timeType || '';
    }
  };

  const getLeaveTypeLabel = (type) =>
    type === 'annual' ? 'Annual Leave' : type === 'medical' ? 'Medical Leave' : 'Time Off';

  const getLeaveTypeColor = (type) =>
    type === 'annual' ? '#4CAF50' : type === 'medical' ? '#FF9800' : '#2196F3';

  const statusBadge = (status) => {
    const s = status || 'pending';
    const st = s === 'approved' ? { bg: '#d4edda', color: '#155724', border: '#c3e6cb' }
      : s === 'rejected' ? { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb' }
      : { bg: '#fff3cd', color: '#856404', border: '#ffeaa7' };
    return (
      <span style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}`, padding: '3px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, textTransform: 'capitalize' }}>
        {s}
      </span>
    );
  };

  // Derive leave summary directly from leaveRecords on every render — no state, no effect, no stale closure.
  let _annualUsed = 0, _medicalUsed = 0, _timeOffHrs = 0;
  leaveRecords.forEach(r => {
    if (r.status !== 'approved') return;
    if (r.type === 'annual')       _annualUsed  += calculateLeaveDays(r);
    else if (r.type === 'medical') _medicalUsed += calculateLeaveDays(r);
    else if (r.type === 'timeoff') _timeOffHrs  += calculateTimeOffHours(r);
  });
  const leaveSummary = {
    annualLeaveUsed:       _annualUsed,
    medicalLeaveUsed:      _medicalUsed,
    timeOffHours:          _timeOffHrs,
    annualLeaveRemaining:  (leaveAllocation?.annualLeave  || 0) - _annualUsed,
    medicalLeaveRemaining: (leaveAllocation?.medicalLeave || 0) - _medicalUsed,
  };

  const today     = new Date().toISOString().split('T')[0];
  const maxDate   = new Date(); maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateStr = maxDate.toISOString().split('T')[0];
  const timeOpts   = generateTimeOptions();

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="staff-portal-container">

      {/* ── HEADER ── */}
      <div className="admin-header-logo">
        <div className="admin-header-logo-content">

          {/* Left: BespokeBeauty wordmark + SalonID */}
          <div className="admin-header-logo-text">
            <h2>BespokeBeauty</h2>
            <small>SalonID: {salonId}</small>
          </div>
          <div className="dashboard-title-with-links">
            <h1>Staff Portal</h1>
          </div>

          {/* Right zone: notification bell + profile */}
          <div className="header-right-zone">

            {/* Quick-action pill: scroll to Leave Summary */}
            <button
              className="header-quick-action-btn"
              onClick={() => leaveSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              title="View leave summary"
            >
              Leave Summary
            </button>

            {/* Notification bell — approved leave alerts */}
            {(() => {
              const activeAlerts = approvedLeaveAlerts.filter(l => !dismissedAlertIds.has(l.id));
              const totalAlerts = activeAlerts.length;
              return (
                <button
                  className="header-notification-btn"
                  onClick={() => setShowAlertsModal(true)}
                  title={totalAlerts > 0 ? `${totalAlerts} approved leave alert${totalAlerts > 1 ? 's' : ''}` : 'No outstanding alerts'}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {totalAlerts > 0 && (
                    <span className="header-notification-badge">
                      {totalAlerts > 99 ? '99+' : totalAlerts}
                    </span>
                  )}
                </button>
              );
            })()}

            {/* Profile avatar + dropdown */}
            <div className="header-profile-wrapper" ref={profileMenuRef}>
              <button
                className="header-profile-btn"
                onClick={() => setShowProfileMenu(prev => !prev)}
                title="Staff profile"
              >
                {currentUser?.email ? currentUser.email[0].toUpperCase() : 'S'}
              </button>
              {showProfileMenu && (
                <div className="header-profile-menu">
                  <div className="header-profile-menu-header">
                    <p className="header-profile-menu-name">
                      {currentUser?.name || 'Staff'}
                    </p>
                    <p className="header-profile-menu-email">
                      {currentUser?.email || ''}
                    </p>
                  </div>
                  <div className="header-profile-menu-divider" />
                  <button
                    className="header-profile-menu-item danger"
                    onClick={() => { setShowProfileMenu(false); handleSignOut(); }}
                    disabled={signingOut}
                  >
                    ↪ {signingOut ? 'Signing out...' : 'Sign Out'}
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Alert bar — approved leave */}
      {(() => {
        const activeAlerts = approvedLeaveAlerts.filter(l => !dismissedAlertIds.has(l.id));
        if (activeAlerts.length === 0) return null;
        return (
          <div className="admin-alert-bar">
            <button className="alert-bar-item" onClick={() => setShowAlertsModal(true)}>
              <span className="alert-bar-dot coral" />
              <span>
                <strong>{activeAlerts.length}</strong> leave request{activeAlerts.length !== 1 ? 's' : ''} approved
              </span>
              <span className="alert-bar-hint">→ View</span>
            </button>
            <button
              className="alert-bar-dismiss"
              onClick={() => activeAlerts.forEach(l => dismissAlert(l.id))}
              aria-label="Dismiss alerts"
            >
              ✕
            </button>
          </div>
        );
      })()}

      {/* Alerts modal */}
      {showAlertsModal && (() => {
        const activeAlerts = approvedLeaveAlerts.filter(l => !dismissedAlertIds.has(l.id));
        const totalAlerts = activeAlerts.length;
        const fmtDate = (d) => d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
        const fmtLeaveType = (t) => t === 'annual' ? 'Annual Leave' : t === 'medical' ? 'Medical Leave' : 'Time Off';
        return (
          <div className="alerts-modal-overlay" onClick={() => setShowAlertsModal(false)}>
            <div className="alerts-modal" onClick={e => e.stopPropagation()}>
              <div className="alerts-modal-header">
                <h2 className="alerts-modal-title">
                  Approved Leave
                  {totalAlerts > 0 && <span className="alerts-modal-count">{totalAlerts}</span>}
                </h2>
                <button className="alerts-modal-close" onClick={() => setShowAlertsModal(false)} aria-label="Close">✕</button>
              </div>

              {totalAlerts === 0 ? (
                <div className="alerts-modal-empty">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  <p>No outstanding alerts</p>
                </div>
              ) : (
                <div className="alerts-modal-body">
                  <section className="alerts-section">
                    <div className="alerts-section-header">
                      <span className="alerts-section-dot coral" />
                      <h3>Approved Leave Requests <span className="alerts-section-count">{totalAlerts}</span></h3>
                    </div>
                    <table className="alerts-table">
                      <thead>
                        <tr>
                          <th>Leave Type</th>
                          <th>Period</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeAlerts.map(l => (
                          <tr key={l.id}>
                            <td><span className="alerts-table-primary">{fmtLeaveType(l.type)}</span></td>
                            <td>{fmtDate(l.startDate)} – {fmtDate(l.endDate)}</td>
                            <td className="alerts-table-actions">
                              <button className="alerts-action-btn ghost" onClick={() => dismissAlert(l.id)}>Dismiss</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── WIDE BODY ── */}
      <div className="staff-portal-wide-body">

        {/* Welcome */}
        <div className="staff-portal-welcome" style={{ textAlign: 'left', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', color: '#E64C31', fontFamily: 'Familjen Grotesk, sans-serif', fontWeight: 700, margin: '0 0 6px', borderBottom: '2px solid #FFBDB1', paddingBottom: '10px' }}>
            Welcome{currentUser?.name ? `, ${currentUser.name}` : ''}
          </h1>
          <p style={{ margin: '8px 0 0', fontFamily: 'Wix Madefor Text, sans-serif', fontSize: '15px', color: '#666' }}>
            Staff Portal
          </p>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 1 — LEAVE REQUEST FORM
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="sp-section">
          <div className="sp-section-header">
            <h2 className="sp-section-title">Add New Leave Record</h2>
            {!showForm && (
              <button
                className="staff-portal-action-btn"
                onClick={() => { setShowForm(true); setFormError(''); setFormSuccess(''); }}
              >
                + Add Leave Record
              </button>
            )}
          </div>

          {formError && (
            <div className="staff-portal-error" style={{ whiteSpace: 'pre-line' }}>{formError}</div>
          )}
          {formSuccess && (
            <div className="staff-portal-success">{formSuccess}</div>
          )}

          {showForm && (
            <div className="staff-leave-form-card">
              <h3>New Leave Request</h3>
              <form onSubmit={handleSubmit}>
                {/* Row 1: Leave Type + Time Type */}
                <div className="staff-form-row">
                  <div className="staff-form-group">
                    <label>Leave Type <span className="sp-required">*</span></label>
                    <select name="type" value={formData.type} onChange={handleInputChange}>
                      <option value="annual">Annual Leave</option>
                      <option value="medical">Medical Leave</option>
                      <option value="timeoff">Time Off</option>
                    </select>
                  </div>
                  <div className="staff-form-group">
                    <label>Time Type <span className="sp-required">*</span></label>
                    <select name="timeType" value={formData.timeType} onChange={handleInputChange}>
                      {formData.type === 'timeoff' ? (
                        <>
                          <option value="custom">Custom Time (1+ hours)</option>
                          <option value="am">Morning (9AM–1PM)</option>
                          <option value="pm">Afternoon (1PM–End)</option>
                          <option value="full">Full Day</option>
                        </>
                      ) : (
                        <>
                          <option value="full">Full Day</option>
                          <option value="am">Morning (9AM–1PM)</option>
                          <option value="pm">Afternoon (1PM–End)</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                {/* Custom time selectors */}
                {formData.type === 'timeoff' && formData.timeType === 'custom' && (
                  <div className="staff-form-row">
                    <div className="staff-form-group">
                      <label>Start Time <span className="sp-required">*</span></label>
                      <select name="startTime" value={formData.startTime} onChange={handleInputChange}>
                        {timeOpts.map(t => <option key={`s-${t}`} value={t}>{formatTime12Hour(t)}</option>)}
                      </select>
                    </div>
                    <div className="staff-form-group">
                      <label>End Time <span className="sp-required">*</span></label>
                      <select name="endTime" value={formData.endTime} onChange={handleInputChange}>
                        {timeOpts.filter(t => timeToMinutes(t) > timeToMinutes(formData.startTime))
                          .map(t => <option key={`e-${t}`} value={t}>{formatTime12Hour(t)}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {/* Start + End date */}
                <div className="staff-form-row">
                  <div className="staff-form-group">
                    <label>Start Date <span className="sp-required">*</span></label>
                    <input type="date" name="startDate" value={formData.startDate} min={today} max={maxDateStr} onChange={handleInputChange} required />
                  </div>
                  <div className="staff-form-group">
                    <label>End Date <span className="sp-required">*</span></label>
                    <input type="date" name="endDate" value={formData.endDate} min={formData.startDate || today} max={maxDateStr} onChange={handleInputChange} required />
                  </div>
                </div>

                {/* Reason */}
                <div className="staff-form-group">
                  <label>Reason <span className="sp-required">*</span></label>
                  <input
                    type="text"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    placeholder={formData.type === 'timeoff' ? 'e.g. Personal appointment, Extended lunch' : 'e.g. Family vacation, Medical procedure'}
                    required
                  />
                </div>

                {/* Additional Notes (optional) */}
                <div className="staff-form-group">
                  <label>Additional Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} placeholder="Any additional information (optional)" />
                </div>

                {/* Mandatory note */}
                <p style={{ fontFamily: 'Wix Madefor Text, sans-serif', fontSize: '13px', color: '#888', margin: '0 0 16px' }}>
                  Fields marked with <span className="sp-required">*</span> are mandatory.
                </p>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="submit" className="staff-portal-action-btn" disabled={saving}>
                    {saving ? 'Checking & Submitting…' : 'Submit Leave Request'}
                  </button>
                  <button type="button" className="staff-portal-cancel-btn" onClick={() => { setShowForm(false); setFormError(''); }} disabled={saving}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {!showForm && !formSuccess && (
            <p style={{ fontFamily: 'Wix Madefor Text, sans-serif', fontSize: '14px', color: '#888', margin: '8px 0 0' }}>
              Click "+ Add Leave Record" to submit a new leave request.
            </p>
          )}
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 2 — CALENDAR
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="sp-section">
          <div className="sp-section-header">
            <h2 className="sp-section-title">My Schedule &amp; Calendar</h2>
          </div>
          <div className="sp-calendar-note">
            <strong>Note:</strong> The calendar below shows your confirmed and pending appointments alongside your leave records for the current week.
          </div>
          {effectiveId ? (
            <StaffCalendarView salonId={salonId} staffId={effectiveId} />
          ) : (
            <p style={{ color: '#888', fontFamily: 'Wix Madefor Text, sans-serif' }}>Unable to load calendar – staff ID not found.</p>
          )}
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 3 — LEAVE SUMMARY + RECORDS
        ═══════════════════════════════════════════════════════════════════════ */}
        <section className="sp-section">
          {/* Leave Summary */}
          <div className="leave-summary-single-box" ref={leaveSummaryRef}>
            <h3>Leave Summary for {currentUser?.name || 'Staff'} ({selectedYear})</h3>

            <div className="leave-header">
              <div className="year-selector">
                <label>Year: </label>
                <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
                  {Array.from({ length: 5 }, (_, i) => {
                    const yr = new Date().getFullYear() - 2 + i;
                    return <option key={yr} value={yr}>{yr}</option>;
                  })}
                </select>
              </div>
            </div>

            <div className="leave-summary-stats">
              <div className="leave-stat-card">
                <h4>Annual Leave</h4>
                <div className="leave-stat-number">{leaveSummary.annualLeaveUsed}</div>
                <div className="leave-stat-label">of {leaveAllocation?.annualLeave || 0} days used</div>
                <small>({leaveSummary.annualLeaveRemaining} remaining)</small>
              </div>
              <div className="leave-stat-card">
                <h4>Medical Leave</h4>
                <div className="leave-stat-number">{leaveSummary.medicalLeaveUsed}</div>
                <div className="leave-stat-label">of {leaveAllocation?.medicalLeave || 0} days used</div>
                <small>({leaveSummary.medicalLeaveRemaining} remaining)</small>
              </div>
              <div className="leave-stat-card">
                <h4>Time Off</h4>
                <div className="leave-stat-number">{leaveSummary.timeOffHours}</div>
                <div className="leave-stat-label">hours taken (no limit)</div>
              </div>
            </div>
          </div>

          {/* Leave Records */}
          <div className="leave-records-section" style={{ marginTop: '24px' }}>
            <h3>Leave Records</h3>

            {leaveLoading ? (
              <div style={{ padding: '20px', color: '#888', fontFamily: 'Wix Madefor Text, sans-serif' }}>Loading leave records…</div>
            ) : leaveRecords.length === 0 ? (
              <div className="no-leave-records">No leave records found for {selectedYear}.</div>
            ) : (
              <div className="records-list">
                {leaveRecords.map(record => (
                  <div key={record.id} className="leave-record-item">
                    <div className="record-header">
                      <div className="record-type">
                        <span
                          className={`leave-type-badge leave-type-${record.type}`}
                          style={{ backgroundColor: getLeaveTypeColor(record.type) }}
                        >
                          {getLeaveTypeLabel(record.type)}
                        </span>
                        <span className="time-type">{formatLeaveTimeType(record)}</span>
                      </div>
                      <div className="record-dates">
                        {record.startDate?.seconds && record.endDate?.seconds ? (
                          <>
                            {formatDate(record.startDate)} – {formatDate(record.endDate)}
                            <small>
                              ({record.type === 'timeoff'
                                ? `${calculateTimeOffHours(record)} hrs`
                                : `${calculateLeaveDays(record)} days`})
                            </small>
                          </>
                        ) : <span>Invalid date</span>}
                      </div>
                    </div>
                    <div className="record-content">
                      <p><strong>Reason:</strong> {record.reason}</p>
                      {record.notes && <p><strong>Notes:</strong> {record.notes}</p>}
                      {record.status && (
                        <p><strong>Status:</strong> {statusBadge(record.status)}</p>
                      )}
                      {record.createdAt?.seconds && (
                        <small>Submitted: {formatDate(record.createdAt)}</small>
                      )}
                    </div>

                    {/* Withdraw button — only for active (approved/pending) future leave */}
                    {(() => {
                      if (!['approved', 'pending'].includes(record.status)) return null;
                      const endDate = record.endDate?.seconds
                        ? new Date(record.endDate.seconds * 1000)
                        : null;
                      const todayStart = new Date();
                      todayStart.setHours(0, 0, 0, 0);
                      if (!endDate || endDate < todayStart) return null;
                      return (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={() => handleWithdrawLeave(record)}
                            style={{
                              background: 'none',
                              border: '1px solid #dc3545',
                              color: '#dc3545',
                              borderRadius: '6px',
                              padding: '5px 14px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                            }}
                          >
                            Withdraw Leave Request
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

      </div>{/* /wide-body */}
    </div>
  );
}

export default StaffPortal;
